// services/productSuggestion.service.js
//
// The PRODUCT-level door: `POST /api/v1/products/{id}/suggest-supplier`
// (BE→FE brief `2026-09-09-product-level-suggestion-and-oos-feed.md`).
//
// ── WHY A SECOND DOOR AT ALL ────────────────────────────────────────────────
//
// The line-level door lives on `order-box-items` and can only be opened by
// someone who already ordered the thing. That refused the one person the
// «مش متوفر» badge is actually FOR: the member reading it on a post *before* she
// orders, who may well know exactly where to get it. This door is on the
// product, so she can say "I know a shop" for anything she can see.
//
// ── THE THREE DIFFERENCES FROM THE LINE-LEVEL DOOR ──────────────────────────
//
//  1. `intent` IS PROHIBITED — sending it is a 422. It was always prohibited
//     here (there is no line to retry), and since 2026-09-15 it is prohibited on
//     the LINE door too, along with `supplier_name` and `any_price` (owner
//     answer H-C). Both doors now take the same three keys; this difference has
//     collapsed into the rule and is kept only as the note that it did.
//  2. WHO MAY OPEN IT is exactly the rule behind `GET /api/v1/suppliers` — the
//     roster she picks a shop from — so if she can read the picker she can
//     submit through this door. A member with no membership, one still under
//     review, and a Suppliers Club member each get a 403.
//  3. SAYING IT TWICE CORRECTS HER TIP IN PLACE, keeping `created_at`, because
//     the leader's desk sorts oldest-waiting-first and correcting herself must
//     not send her to the back of the queue. So the app offers "change what I
//     sent" rather than a second door.
//
// The 200 hands back the product resource carrying `mySuggestion`, so nothing
// re-fetches the product to find out what was recorded.
//
// NOT gated on the product being out of stock — the app decides WHERE to show
// the door (beside the badge); the API does not enforce it. That is deliberate:
// a mark cleared while her sheet is open must not turn her tap into a 403.
import { jsonApiClient } from '@/httpConfig/config/axios.js';
import { PRICE_SEEN_MIN_CENTS } from '@/utils/supplierSuggestion.js';

/** The door refused her outright: no membership, still under review, rejected,
 *  or a Suppliers Club member. Not about what she typed, so the host closes the
 *  sheet rather than leaving her staring at a field she cannot fix. */
export const PRODUCT_SUGGEST_FORBIDDEN = 'product-suggest-forbidden';

/** A validation refusal — a missing or unknown shop id, a note over 500, or a
 *  price under one pound.
 *  Its `detail` is the backend's own sentence and is always better than ours. */
export const PRODUCT_SUGGEST_INVALID = 'product-suggest-invalid';

function normalizeError(error) {
  const detail = error?.response?.data?.errors?.[0]?.detail || null;
  const normalized = new Error(detail || error?.message || 'Request failed');
  normalized.status = error?.response?.status;
  normalized.detail = detail;
  normalized.original = error;

  if (normalized.status === 403) {
    normalized.code = PRODUCT_SUGGEST_FORBIDDEN;
  } else if (normalized.status === 422) {
    normalized.code = PRODUCT_SUGGEST_INVALID;
  }
  return normalized;
}

/**
 * Tell the club where to find a product she has NOT ordered.
 *
 * A plain body, not a JSON:API document — the same shape the line-level door
 * takes, because the backend validates plain snake_case keys.
 *
 * ── THE SAME THREE KEYS THE LINE DOOR TAKES ─────────────────────────────────
 * (oos-hold P1-B, brief `2026-09-15-roster-only-tips-once-per-line.md`, owner
 * answer H-C.) `supplier_id` REQUIRED from the roster, optional `note`,
 * optional `price_seen_cents` in piasters at or above 100. `supplier_name` and
 * `any_price` now 422 — they are not accepted as arguments at all, for the same
 * reason `intent` never was: a key this function cannot build is a 422 it
 * cannot cause.
 *
 * This door is NOT once-only, unlike the line door: one open tip per member and
 * product, and an answered one does not block a new one.
 *
 * @param {string|number} productId
 * @param {{supplierId: string|number, note?: string|null,
 *   priceSeenCents?: number|null}} suggestion
 * @returns {Promise<object>} the updated product, flat (deserialized) JSON:API,
 *   already carrying `mySuggestion`.
 * @throws Error with `code` set to one of the two tags above.
 */
export async function suggestSupplierForProduct(
  productId,
  { supplierId, note, priceSeenCents } = {},
) {
  const body = {};

  // REQUIRED, and from the club's roster. An absent id is left to the backend's
  // own 422 rather than invented here.
  if (supplierId != null && supplierId !== '') {
    body.supplier_id = Number(supplierId);
  }

  if (typeof note === 'string' && note.trim() !== '') {
    body.note = note.trim();
  }

  // Piasters, whole, at or above the floor — anything else is DROPPED rather
  // than sent, because she left the field optional.
  if (Number.isInteger(priceSeenCents) && priceSeenCents >= PRICE_SEEN_MIN_CENTS) {
    body.price_seen_cents = priceSeenCents;
  }

  // NO `intent`, `supplier_name` or `any_price` KEY, EVER — see the docblock.
  // This function does not accept those arguments, which is why a shared sheet
  // spreading one payload into both doors cannot regress into a 422.

  try {
    const response = await jsonApiClient.post(
      `/products/${productId}/suggest-supplier`,
      body,
    );
    const data = response?.data?.data;
    if (!data) {
      throw new Error('Empty response from the product suggest-supplier endpoint');
    }
    return data;
  } catch (error) {
    throw normalizeError(error);
  }
}
